import React from 'react';
import { Descriptions, Table } from 'antd';
import type { ProductImageGeneration } from '@/api/ProductImages/types';
import { useTranslate } from '@/translations';
import { formatImageryTime } from '@/modules/ProductImages/GenerationRecord';
import {
  CandidateIdentityAssessment,
  storedEvaluationChecks,
} from '@/modules/ProductImages/IdentityAssessment';

export const GenerationAudit = ({
  record,
  layout = 'default',
}: {
  record: ProductImageGeneration;
  layout?: 'default' | 'fullWidth';
}) => {
  const { t } = useTranslate();
  const unknown = t('imagery.unknown');
  return (
    <div
      className={layout === 'fullWidth' ? 'imagery-variant-audit' : undefined}
    >
      <Descriptions
        bordered={layout === 'default'}
        size="small"
        layout={layout === 'fullWidth' ? 'vertical' : 'horizontal'}
        column={layout === 'fullWidth' ? { xs: 1, sm: 2, lg: 3 } : 1}
        items={[
          {
            key: 'error',
            label: t('imagery.errorCode'),
            children: record.errorCode || '-',
          },
          {
            key: 'source',
            label: t('imagery.sourceRevision'),
            children: record.sourceRevisionId,
          },
          {
            key: 'requested',
            label: t('imagery.requestedAt'),
            children: formatImageryTime(record.requestedAt),
          },
          {
            key: 'started',
            label: t('imagery.startedAt'),
            children: formatImageryTime(record.startedAt),
          },
          {
            key: 'completed',
            label: t('imagery.completedAt'),
            children: formatImageryTime(record.completedAt),
          },
          {
            key: 'reason',
            label: t('imagery.reason'),
            children: record.reason || '-',
          },
          {
            key: 'step',
            label: t('imagery.currentStep'),
            children: record.currentStep || '-',
          },
          {
            key: 'requests',
            label: t('imagery.modelRequests'),
            children: record.modelRequestCount ?? unknown,
          },
          {
            key: 'cost',
            label: t('imagery.estimatedCost'),
            children:
              record.estimatedCost == null
                ? unknown
                : `$${record.estimatedCost.toFixed(4)}`,
          },
          {
            key: 'score',
            label: t('imagery.score'),
            children:
              typeof record.evaluation?.score === 'number'
                ? `${record.evaluation.score}/100`
                : unknown,
          },
          {
            key: 'hardRejection',
            label: t('imagery.hardRejection'),
            children:
              typeof record.evaluation?.hardReject === 'boolean'
                ? record.evaluation.hardReject
                  ? 'Yes'
                  : 'No'
                : unknown,
          },
          {
            key: 'evaluation',
            label: t('imagery.checks'),
            children:
              typeof record.evaluation?.rejectionReason === 'string'
                ? record.evaluation.rejectionReason
                : unknown,
          },
        ]}
      />
      <CandidateIdentityAssessment
        checks={storedEvaluationChecks(record.evaluation)}
        recipeVersion={record.recipeVersion}
      />
      <h4 className="mt-3">{t('imagery.usage')}</h4>
      <Table
        size="small"
        pagination={false}
        rowKey="rowKey"
        scroll={{ x: 560 }}
        dataSource={(record.usage ?? []).map((usage, index) => ({
          ...usage,
          rowKey: index,
        }))}
        columns={[
          { title: t('imagery.step'), dataIndex: 'step' },
          {
            title: t('imagery.model'),
            dataIndex: 'model',
            render: (value) => value ?? unknown,
          },
          {
            title: t('imagery.inputTokens'),
            dataIndex: 'inputTokens',
            render: (value) => value ?? unknown,
          },
          {
            title: t('imagery.outputTokens'),
            dataIndex: 'outputTokens',
            render: (value) => value ?? unknown,
          },
          { title: t('imagery.result'), dataIndex: 'outcome' },
          {
            title: t('imagery.estimatedCost'),
            dataIndex: 'estimatedCost',
            render: (value) =>
              value == null ? unknown : `$${Number(value).toFixed(4)}`,
          },
        ]}
      />
      <h4 className="mt-3">{t('imagery.stageDurations')}</h4>
      <Table
        size="small"
        pagination={false}
        rowKey="rowKey"
        scroll={layout === 'fullWidth' ? { x: 400 } : undefined}
        dataSource={(record.stageDurations ?? []).map((duration, index) => ({
          ...duration,
          rowKey: index,
        }))}
        columns={[
          { title: t('imagery.step'), dataIndex: 'step' },
          {
            title: t('imagery.duration'),
            dataIndex: 'milliseconds',
            render: (value) =>
              typeof value === 'number'
                ? `${(value / 1000).toFixed(1)} s`
                : unknown,
          },
        ]}
      />
    </div>
  );
};
